// ── All Borrowers / Guarantors — global list, and the perfection review ──
//
// Two jobs in one screen, on purpose.
//
// The list has always answered "who are the legal entities behind our
// facilities". Those entities are also the DEBTORS a UCC financing statement
// names, and a filing is made in the state where the entity is REGISTERED — not
// where it operates. Every row's stateoforganization was seeded from its
// account's headquarters state, which is right for most borrowers and wrong for
// exactly the ones where it matters: a Delaware LLC run out of North Carolina.
//
// So the review lives here rather than on a page of its own. It is the same
// eighty-odd rows either way, and a separate screen would mean two places that
// disagree about the same entity.
//
// THERE IS NO "CONFIRM ALL". Deliberately. The value of the column is that
// somebody looked at a charter; a button that ticks eighty rows at once would
// convert it back into the guess it started as, while making it look verified.
// Each row is one click, and the master save commits them together.
import { useState, useEffect } from "react";
import { localFilter, exportCSV, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, SearchBar, Btn, Card, STh, Th, Td, Badge, Icon, Pagination } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";
import { US_STATE_NAMES } from "../../../shared/usStates";
import { stateOfOrgState, StateOfOrgBadge } from "../../../shared/entityIdentity";

const STATE_CODES = Object.keys(US_STATE_NAMES)
  .sort((a, b) => US_STATE_NAMES[a].localeCompare(US_STATE_NAMES[b]));

export const AllBorrowersGuarantorsPage = () => {
      const { api, setModal, role, setConfirm, delRec } = useApp();
      const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true); const [err,setErr]=useState(null);
      const [q,setQ]=useState("");
      // Active accounts only by default; "Show all accounts" brings back paid
      // off, charged off and the rest.
      const [allAccts,setAllAccts]=useState(false);

      // ── Review state ──
      // edits maps entityid -> { state?, confirm? }. Nothing is written until
      // the master save, so a half-finished screen can be abandoned without
      // leaving the book half-confirmed.
      const [review,setReview]=useState(false);
      const [edits,setEdits]=useState({});
      const [saving,setSaving]=useState(false);
      const [note,setNote]=useState(null);

      const load=()=>{setLoading(true);api.get("getBorrowersGuarantors").then(d=>{setRows(Array.isArray(d)?d:[]);setLoading(false);}).catch(e=>{setErr(e.message);setLoading(false);});};
      useEffect(()=>{load();},[]);

      // What the row would look like with the pending edit applied — the badge
      // has to react as you work, or you cannot see what you are committing.
      const pending = bg => {
        const e = edits[bg.id];
        if (!e) return bg;
        return {
          ...bg,
          stateoforganization: e.state !== undefined ? e.state : bg.stateoforganization,
          // A tick is a confirmation that has not happened yet; showing it as
          // confirmed would be a lie, so it gets its own colour below instead.
          stateoforg_confirmedat: bg.stateoforg_confirmedat,
        };
      };

      const needsReview = bg =>
        bg.entitytype !== "Individual"
        && ["provisional","missing"].includes(stateOfOrgState(bg).key);

      const outstanding = rows.filter(r => allAccts || ["Accrual","Non Accrual","Funding in Progress"].includes(r.accountstatus)).filter(needsReview).length;

      // Active = funded and not closed out, plus deals still funding.
      const ACTIVE = ["Accrual","Non Accrual","Funding in Progress"];
      const scoped = allAccts ? rows : rows.filter(r => ACTIVE.includes(r.accountstatus));
      const inactiveCount = rows.length - rows.filter(r => ACTIVE.includes(r.accountstatus)).length;
      const base = review ? scoped.filter(needsReview) : scoped;
      const filtered=localFilter(base,q,["companyname","accountnumber","borrowerguarantorname","entitytype","borrowerguarantor","stateoforganization"]);
      const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, 'companyname');
      const { paginated:displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);

      const setEdit = (id, patch) =>
        setEdits(m => ({ ...m, [id]: { ...(m[id]||{}), ...patch } }));

      // Only rows that actually carry a change. Ticking and unticking a box
      // leaves an entry behind in `edits`, and sending it would re-stamp a
      // confirmation date for no reason.
      const dirty = Object.keys(edits).filter(id => {
        const e = edits[id] || {};
        const bg = rows.find(r => r.id === id);
        if (!bg) return false;
        const stateChanged = e.state !== undefined
          && String(e.state||"").toUpperCase() !== String(bg.stateoforganization||"").toUpperCase();
        return stateChanged || !!e.confirm;
      });

      const save = async () => {
        setSaving(true); setNote(null);
        try {
          const items = dirty.map(id => {
            const bg = rows.find(r => r.id === id); const e = edits[id]||{};
            return {
              id,
              stateoforganization: e.state !== undefined ? e.state : (bg.stateoforganization||""),
              confirm: !!e.confirm,
            };
          });
          const res = await api.post("confirmStateOfOrganization", { items });
          setEdits({});
          await new Promise(r => { load(); setTimeout(r, 0); });
          const failed = (res.results||[]).filter(x=>!x.ok);
          setNote(res.written + " entit" + (res.written===1?"y":"ies") + " updated"
            + (failed.length ? " · " + failed.length + " rejected: " + failed.map(f=>f.error).join("; ") : ""));
        } catch (e) { setNote("Nothing was saved — " + e.message); }
        setSaving(false);
      };

      if(loading) return <Spinner />; if(err) return <ErrorMsg msg={err} onRetry={load} />;

      const selStyle = { padding:"4px 6px", fontSize:12, borderRadius:4, border:"1px solid #999", background:"#fff" };

      return <>
        <SectionHeader actions={[
          <SearchBar key="s" value={q} onChange={v=>{ setQ(v); reset(); }} placeholder="Search borrowers/guarantors..." />,
          <Btn key="all" variant={allAccts?"primary":undefined} onClick={()=>{ setAllAccts(x=>!x); reset(); }}
               title="By default only Accrual, Non Accrual and Funding in Progress accounts are listed">
            {allAccts ? "Active accounts only" : "Show all accounts" + (inactiveCount ? " (+" + inactiveCount + ")" : "")}
          </Btn>,
          role.canEdit && <Btn key="rv" variant={review?"primary":undefined}
            onClick={()=>{ setReview(r=>!r); setQ(""); reset(); setNote(null); }}>
            {review ? "Show all" : "Review state of org" + (outstanding ? " (" + outstanding + ")" : "")}
          </Btn>,
          <Btn key="ex" onClick={()=>exportCSV(sorted,["companyname","accountnumber","accountstatus","borrowerguarantorname","borrowerguarantor","entitytype","stateoforganization","stateoforg_confirmedat","stateoforg_confirmedby","createdat"],"borrowers_guarantors.csv")}>Export CSV</Btn>
        ].filter(Boolean)} />

        {review && (
          <div style={{ fontSize:12, color:"#7a5200", background:"#FAEEDA", border:"0.5px solid #EBD3A0",
                        borderRadius:8, padding:"10px 14px", marginBottom:12 }}>
            <strong>State of organization decides where a UCC-1 has to be filed.</strong>{" "}
            These values were copied from each account's headquarters state as a starting point. A company
            can operate in one state and be registered in another, and a financing statement filed in the
            wrong one perfects nothing. Check each against the entity's charter, then tick it.
          </div>
        )}

        {note && (
          <div style={{ fontSize:12, background:"var(--color-background-secondary)",
                        border:"0.5px solid var(--color-border-tertiary)", borderRadius:8,
                        padding:"8px 12px", marginBottom:12 }}>{note}</div>
        )}

        {review && dirty.length > 0 && (
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <Btn variant="primary" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save " + dirty.length + " change" + (dirty.length===1?"":"s")}
            </Btn>
            <Btn disabled={saving} onClick={()=>setEdits({})}>Discard</Btn>
            <span style={{ fontSize:12, color:"var(--color-text-secondary)" }}>
              Unsaved. Moving between pages keeps them.
            </span>
          </div>
        )}

        <Card style={{ overflowX:"auto" }}>
          <table style={{ width:"100%",borderCollapse:"collapse", whiteSpace:"nowrap" }}>
            <thead><tr>
              <STh label="Account"     sortKey="companyname"           currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Account #"   sortKey="accountnumber"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Status"      sortKey="accountstatus"         currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Name"        sortKey="borrowerguarantorname" currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Role"        sortKey="borrowerguarantor"     currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="Entity Type" sortKey="entitytype"            currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <STh label="State of Org" sortKey="stateoforganization"  currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              {review && <Th>Verified</Th>}
              <Th>TIN</Th>
              <STh label="Created"     sortKey="createdat"             currentKey={sortKey} currentDir={sortDir} onSort={toggle} />
              <Th></Th>
            </tr></thead>
            <tbody>{displayed.map(bg=>{
              const e = edits[bg.id] || {};
              const stateVal = e.state !== undefined ? e.state : (bg.stateoforganization || "");
              const isIndividual = bg.entitytype === "Individual";
              return (
              <tr key={bg.id} style={{ cursor:"pointer" }} onClick={()=>setModal({type:"borrowerguarantor-view",rec:{...bg,id:bg.id,accountid:bg.accountid}})} onMouseEnter={e2=>e2.currentTarget.style.background="var(--color-background-secondary)"} onMouseLeave={e2=>e2.currentTarget.style.background=""}>
                <Td style={{ fontWeight:500 }}>{bg.companyname}</Td>
                <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{bg.accountnumber||"—"}</span></Td>
                <Td>{bg.accountstatus ? <Badge s={bg.accountstatus} /> : "—"}</Td>
                <Td>{bg.borrowerguarantorname||"—"}</Td>
                <Td><Badge s={bg.borrowerguarantor} /></Td>
                <Td>{bg.entitytype||"—"}</Td>

                {/* Editable only in review mode. Outside it this is a badge, so
                    the list stays a list and nobody changes a filing jurisdiction
                    by clicking the wrong cell while scanning. */}
                <Td onClick={e2=>{ if(review) e2.stopPropagation(); }}>
                  {review && role.canEdit && !isIndividual ? (
                    <select value={stateVal} style={selStyle}
                            onChange={e2=>setEdit(bg.id,{ state:e2.target.value })}>
                      <option value="">— none —</option>
                      {STATE_CODES.map(c=><option key={c} value={c}>{c} — {US_STATE_NAMES[c]}</option>)}
                    </select>
                  ) : <StateOfOrgBadge entity={pending(bg)} />}
                </Td>

                {review && (
                  <Td onClick={e2=>e2.stopPropagation()}>
                    {isIndividual
                      ? <span style={{ color:"var(--color-text-secondary)", fontSize:12 }}>n/a</span>
                      : stateOfOrgState(bg).key === "confirmed" && !e.confirm && e.state === undefined
                        ? <span style={{ fontSize:11.5, color:"#0F6E56" }}
                                title={bg.stateoforg_confirmedby ? "Confirmed by " + bg.stateoforg_confirmedby : "Confirmed"}>
                            ✓ {fmtDate(bg.stateoforg_confirmedat)}
                          </span>
                        : <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:12,
                                          cursor: stateVal ? "pointer" : "default",
                                          opacity: stateVal ? 1 : 0.45 }}>
                            <input type="checkbox" disabled={!stateVal} checked={!!e.confirm}
                                   onChange={e2=>setEdit(bg.id,{ confirm:e2.target.checked })} />
                            checked charter
                          </label>}
                  </Td>
                )}

                {/* Last four only — the full TIN is no longer sent to the browser. */}
                <Td><span style={{ fontFamily:"var(--font-mono)",fontSize:12 }}>{bg.tinlast4 ? "••••"+bg.tinlast4 : "—"}</span></Td>
                <Td>{fmtDate(bg.createdat)}</Td>
                <Td><div style={{ display:"flex",gap:6, justifyContent:"flex-end" }} onClick={e2=>e2.stopPropagation()}>
                  <Btn onClick={()=>setModal({type:"borrowerguarantor-view",rec:{...bg,id:bg.id,accountid:bg.accountid}})}><Icon type="view" /></Btn>
                  {role.canEdit&&<Btn onClick={()=>setModal({type:"borrowerguarantor-edit",rec:{...bg,id:bg.id,accountid:bg.accountid},onDone:load})}><Icon type="edit" /></Btn>}
                  {role.canDelete&&<Btn onClick={()=>setConfirm({msg:"Delete this borrower/guarantor?",onConfirm:()=>delRec("BorrowersGuarantors",bg.id,"Deleted borrower/guarantor",load)})} variant="danger"><Icon type="delete" /></Btn>}
                </div></Td>
              </tr>
            );})}</tbody>
          </table>
          {displayed.length === 0 && (
            <div style={{ padding:20, textAlign:"center", fontSize:13, color:"var(--color-text-secondary)" }}>
              {review ? "Nothing left to review — every entity's state of organization has been confirmed."
                      : "No borrowers or guarantors found."}
            </div>
          )}
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
        </Card>
      </>;
    };
